/**
 * Custom-column cell for `timeline` columns. The value is a list of phases,
 * `{ id, label, start, end }` with ISO `YYYY-MM-DD` dates, drawn as one bar
 * divided in proportion to how long each phase runs.
 *
 * `date_range` already covers a single span; a timeline exists for the several
 * named phases a row moves through. Positioning each phase at its true dates
 * reads better at four phases than at ten, and a short phase collapses to an
 * unreadable sliver, so the bar is continuous and the caption carries the one
 * thing a board scan is after: which phase the row is in right now.
 */
import { Button, Input, Popover, PopoverContent, PopoverTrigger } from '@monksflow/monks-ui';
import { format } from 'date-fns';
import { GanttChart, Plus, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import { parseLocalDate } from '../../lib/parseLocalDate';
import { EM_DASH } from './constants';

/** Persisted shape of one phase in a `timeline` cell value. */
export interface TimelinePhase {
  id: string;
  label: string;
  start: string;
  end: string;
}

interface TimelineCustomCellProps {
  value: TimelinePhase[] | undefined;
  onChange: (next: TimelinePhase[] | null) => void;
  /** Shows the phases with no editor behind them, for someone who cannot change them. */
  readOnly?: boolean;
}

/**
 * Segment fills, cycled by position. Ramp steps rather than status tokens: a
 * phase is a category, and green or red here would read as a health signal.
 */
const PHASE_CLASSES = ['bg-blue-500', 'bg-violet-500', 'bg-amber-500', 'bg-teal-500'];

const MS_PER_DAY = 86_400_000;

interface Segment {
  phase: TimelinePhase;
  width: number;
  elapsed: boolean;
}

interface Schedule {
  segments: Segment[];
  current: TimelinePhase;
  currentPosition: number;
}

/**
 * Orders the phases and sizes each one by its share of the total time booked.
 * Durations are day-inclusive, so a single-day phase still gets a slice instead
 * of dividing by zero.
 *
 * The current phase is the first one not yet finished — finished meaning its
 * end date is behind us, not merely begun, so a phase stays current for the
 * whole of its final day. A row whose entire schedule is in the past sits on
 * its final phase rather than on nothing at all.
 */
function schedule(phases: TimelinePhase[], now: Date): Schedule | undefined {
  const dated = phases
    .flatMap((phase) => {
      const start = parseLocalDate(phase.start);
      const end = parseLocalDate(phase.end);
      return start && end && end >= start ? [{ phase, start, end }] : [];
    })
    .sort((a, b) => a.start.getTime() - b.start.getTime());
  if (dated.length === 0) return undefined;

  const durations = dated.map((d) => d.end.getTime() - d.start.getTime() + MS_PER_DAY);
  const total = durations.reduce((sum, duration) => sum + duration, 0);

  const unfinished = dated.findIndex((d) => d.end.getTime() + MS_PER_DAY > now.getTime());
  const currentIndex = unfinished === -1 ? dated.length - 1 : unfinished;

  return {
    segments: dated.map((d, index) => ({
      phase: d.phase,
      width: (durations[index] / total) * 100,
      elapsed: index < currentIndex,
    })),
    current: dated[currentIndex].phase,
    currentPosition: currentIndex + 1,
  };
}

export function TimelineCustomCell({ value, onChange, readOnly }: TimelineCustomCellProps) {
  const { t } = useTranslation('trackers');

  const [open, setOpen] = useState(false);
  const phases = value ?? [];
  const plotted = schedule(phases, new Date());

  /**
   * The popover edits a draft and writes once, on close. A phase name is typed
   * one character at a time, and sending each one through `onChange` is a cell
   * PATCH per keystroke whose responses are free to land out of order and undo
   * what the person is still typing.
   */
  const [draft, setDraft] = useState<TimelinePhase[]>(phases);
  useEffect(() => {
    if (!open) setDraft(value ?? []);
  }, [value, open]);

  const update = (id: string, patch: Partial<TimelinePhase>) => {
    setDraft((current) =>
      current.map((phase) => (phase.id === id ? { ...phase, ...patch } : phase)),
    );
  };

  const remove = (id: string) => {
    setDraft((current) => current.filter((phase) => phase.id !== id));
  };

  const addPhase = () => {
    const today = format(new Date(), 'yyyy-MM-dd');
    setDraft((current) => [
      ...current,
      {
        id: uuidv4(),
        label: t('trackersBoard.timelineCustomCell.newPhase'),
        start: today,
        end: today,
      },
    ]);
  };

  const commit = () => {
    if (JSON.stringify(draft) === JSON.stringify(phases)) return;
    onChange(draft.length === 0 ? null : draft);
  };

  const display = plotted ? (
    <span className="flex w-full min-w-0 flex-col gap-1">
      <span className="flex h-2 w-full overflow-hidden rounded-full bg-muted">
        {plotted.segments.map(({ phase, width, elapsed }, index) => (
          <span
            key={phase.id}
            className={`h-full ${PHASE_CLASSES[index % PHASE_CLASSES.length]} ${elapsed ? 'opacity-35' : ''}`}
            style={{ width: `${width}%` }}
            title={phase.label}
          />
        ))}
      </span>
      <span className="truncate text-left text-[11px] text-muted-foreground">
        <Trans
          t={t}
          i18nKey="trackersBoard.timelineCustomCell.phaseProgress"
          values={{
            label: plotted.current.label,
            position: plotted.currentPosition,
            total: plotted.segments.length,
          }}
          components={{ phase: <span className="font-medium text-foreground" /> }}
        />
      </span>
    </span>
  ) : readOnly ? (
    <span className="text-muted-foreground">{EM_DASH}</span>
  ) : (
    <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
      <GanttChart className="size-3.5 shrink-0" aria-hidden />
      {t('trackersBoard.timelineCustomCell.setPhases')}
    </span>
  );

  if (readOnly) {
    return <div className="flex w-full items-center justify-center px-1 py-0.5">{display}</div>;
  }

  return (
    <Popover
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) commit();
        setOpen(nextOpen);
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          className="flex w-full items-center justify-center rounded px-1 py-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {display}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-2" onClick={(e) => e.stopPropagation()}>
        {draft.length > 0 ? (
          <ul className="mb-2 flex max-h-56 flex-col gap-1.5 overflow-y-auto">
            {draft.map((phase) => (
              <li key={phase.id} className="flex flex-col gap-1 rounded border border-border p-1.5">
                <div className="flex items-center gap-1">
                  <Input
                    value={phase.label}
                    onChange={(e) => update(phase.id, { label: e.target.value })}
                    placeholder={t('trackersBoard.timelineCustomCell.phaseName')}
                    className="h-7 text-xs"
                  />
                  <button
                    type="button"
                    onClick={() => remove(phase.id)}
                    aria-label={t('trackersBoard.timelineCustomCell.removePhase', {
                      label: phase.label,
                    })}
                    className="shrink-0 rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
                  >
                    <X className="size-3" aria-hidden />
                  </button>
                </div>
                <div className="flex items-center gap-1">
                  <Input
                    type="date"
                    value={phase.start}
                    onChange={(e) => update(phase.id, { start: e.target.value })}
                    aria-label={t('trackersBoard.timelineCustomCell.startDate')}
                    className="h-7 text-xs"
                  />
                  <Input
                    type="date"
                    value={phase.end}
                    onChange={(e) => update(phase.id, { end: e.target.value })}
                    aria-label={t('trackersBoard.timelineCustomCell.endDate')}
                    className="h-7 text-xs"
                  />
                </div>
              </li>
            ))}
          </ul>
        ) : null}
        <Button size="sm" variant="outline" className="w-full" onClick={addPhase}>
          <Plus className="size-3.5" aria-hidden />
          {t('trackersBoard.timelineCustomCell.addPhase')}
        </Button>
      </PopoverContent>
    </Popover>
  );
}
